import dayjs from 'dayjs';
import { DateValue } from '../types';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

export const getYearlyDaysFromDate = (yearly_days: DateValue[]) => {
  return yearly_days?.length ? yearly_days.map((x: DateValue) => dayjs(x.date).format('YYYY-MM-DD')) : [];
};

export const getYearlyDays = (yearly_days: (number | string)[]) => {
  return yearly_days?.length ? yearly_days.map((x: number | string) => dayjs(x).format('YYYY-MM-DD')) : [];
};

export function compare_array_dates(a: { date?: unknown }, b: { date?: unknown }) {
  return Number(a.date) - Number(b.date);
}

export function compare_name_asc(a: { name: string }, b: { name: string }) {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
}

export function compare_name_desc(a: { name: string }, b: { name: string }) {
  if (a.name > b.name) {
    return -1;
  }
  if (a.name < b.name) {
    return 1;
  }
  return 0;
}

interface ExportCSVProps {
  header: string[];
  data: {
    dates?: unknown[];
    rawDates?: string[];
    values?: unknown[];
  };
  fileName?: string;
  dateFormat?: string;
  unit?: string;
  isSoilingSensorData?: boolean;
  sensorType?: string;
}

export const ExportCSV = ({ header, data, fileName, dateFormat, unit, isSoilingSensorData, sensorType }: ExportCSVProps) => {
  const downloadCSV = () => {
    // For soiling sensor data, download the appropriate template based on sensor type
    if (isSoilingSensorData) {
      // Map sensor type to file name
      const sensorTypeMap: { [key: string]: { file: string; name: string } } = {
        rde300: { file: 'rde300.csv', name: 'rde300_template.csv' },
        dustiq: { file: 'dustiq.csv', name: 'dustiq_template.csv' },
        soiling_ratio: { file: 'soiling ratio.csv', name: 'soiling_ratio_template.csv' },
      };

      // Default to rde300 if sensor type is not provided or not recognized
      const templateInfo = sensorTypeMap[sensorType || 'rde300'] || sensorTypeMap['rde300'];

      // Create a download link for the appropriate CSV template file
      const link = document.createElement('a');
      link.href = `/files/${templateInfo.file}`;
      link.download = templateInfo.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Format dates if dateFormat is provided
    let formattedDates = data?.dates;
    if (dateFormat && data?.rawDates) {
      formattedDates = data.rawDates.map((date: string) => {
        const d = new Date(Date.parse(date));
        return dayjs(d).format(dateFormat);
      });
    }

    // Original single-column logic
    // Add unit to the header if provided
    const customHeader = header.map((h: string) => (h === 'values' && unit ? `values (${unit})` : h));
    // Convert the data array into a CSV string
    const csvString = [
      customHeader, // Specify your headers here
      ...(formattedDates?.map((_item, i) => [formattedDates?.[i], data.values?.[i]]) || []), // Map your data fields accordingly
    ]
      .map((row) => row.join(';'))
      .join('\n');

    // Create a Blob from the CSV string
    const blob = new Blob([csvString], { type: 'text/csv' });

    // Generate a download link and initiate the download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button variant="contained" onClick={downloadCSV} startIcon={<DownloadIcon />}>
      Download CSV template
    </Button>
  );
};

export const getSolutionDisplayName = (cleaningPackages: Record<string, { name?: string }>, technicalName: string) => {
  if (!technicalName || !cleaningPackages[technicalName]) {
    return technicalName || 'N/A';
  }
  return cleaningPackages[technicalName].name || technicalName;
};

export const isEmpty = (obj: object) => obj === null || Object.keys(obj).length === 0;

export const getPackageImageMapping = () => {
  return {
    'Manual, Dry': 'Manual_dry',
    'Manual, Wet': 'Manual_wet',
    'Mazaka, Dry': 'Mazaka_dry',
    'Mazaka, Wet': 'Mazaka_wet',
    'SCM, Wet': 'SCM_wet',
    'SunBrush, Dry': 'SunBrush_dry',
    'SunBrush, Wet': 'SunBrush_wet',
    'Sunpure PR300': 'Sunpure_PR300',
    'Solbright Gen7': 'Solbright_gen7',
    'Solar-lit auto': 'Solar_Lit_auto',
    'Solabot SB-01': 'Solabot_SB_01',
    'Sunpure PS100': 'Sunpure_PS100',
    'Solbright Semi-auto': 'Solbright_semi_auto',
    'Solabot SB-03': 'Solabot_SB_03',
    'BPMM, Wet': 'BPMM_wet',
    'BPMM, Dry': 'BPMM_dry',
    // Default fallback
    default: 'BPMM_wet',
  };
};

export const getImageKeyForPackage = (packageName: string): string => {
  const imageMapping = getPackageImageMapping();

  // Check if the package name contains any of the known package names
  for (const [packageKey, imageKey] of Object.entries(imageMapping)) {
    if (packageKey !== 'default' && packageName.includes(packageKey)) {
      return imageKey;
    }
  }

  // Return default if no match found
  return imageMapping['default'];
};

// Validation regex for number input (allow negative numbers and decimal numbers)
export const NUMBER_VALIDATION_REGEX = /^[-]?[0-9]+\.?[0-9]*$|^[-]?[0-9]*$/;

export const isValidNumberInput = (value: string): boolean => {
  return value === '' || NUMBER_VALIDATION_REGEX.test(value);
};

/**
 * Build the query parameters for the /soiling-ratios endpoint (project_id + ex_post context).
 */
// Rounds a derived rain parameter (threshold in mm/day or efficiency in 0-1) for display.
const rainValueFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, useGrouping: false });
export const formatRainValue = (value: number) => rainValueFormat.format(value);

export const buildSoilingRatiosParams = (projectId: string | undefined, role: string) => {
  // Everything (plant/location/weather + uploaded sensor files) is read from the persisted project
  // on the backend. Only project_id and the context flag are needed: the HSU baseline is shown for
  // the operational strategy but not the ex-post analysis, i.e. hsu = not ex_post.
  const params: { ex_post: boolean; project_id?: string } = {
    ex_post: role === 'ex_post_analysis',
  };

  if (projectId) {
    params.project_id = projectId;
  }

  return params;
};

/**
 * Kept as a pass-through: previously this dropped user-uploaded pattern payloads when the value
 * came from the API. But `cleanFormDataForSave` only shallow-copies, so deleting here mutated the
 * live formData and, once the project reloaded, the upload was lost — enabling then disabling the
 * "external source" toggle wiped an already uploaded pattern and failed validation. Operational
 * blocks never dropped theirs and had no such bug, so we now keep every uploaded pattern.
 */
export const cleanUserPatterns = <T,>(data: T): T => {
  return data;
};

interface SoilingSensorBlock {
  file_uid?: string;
  type?: string;
  _order?: number;
  name?: string;
}

/**
 * Transform the dynamic soiling sensor data blocks (soiling_sensor_data, soiling_sensor_data_*)
 * into the array shape the backend expects, excluding their in-memory pattern payloads.
 */
export const cleanFormDataForSave = (data: Record<string, unknown>) => {
  const cleanedData = { ...data };

  // Find all soiling sensor data blocks
  const soilingDataKeys = Object.keys(cleanedData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));

  // Transform soiling sensor data blocks into an array
  const soilingSensorDataArray: SoilingSensorBlock[] = [];

  soilingDataKeys.forEach((blockKey) => {
    const block = cleanedData[blockKey];
    if (block && typeof block === 'object') {
      const blockData = block as SoilingSensorBlock;
      if (blockData.file_uid) {
        soilingSensorDataArray.push({
          file_uid: blockData.file_uid,
          type: blockData.type || 'rde300',
          _order: blockData._order || 0,
          name: blockData.name || '', // Include the name field
        });
      }
      // Remove the individual block from cleanedData
      delete cleanedData[blockKey];
    }
  });

  // Add the array to cleanedData
  if (soilingSensorDataArray.length > 0) {
    cleanedData.soiling_sensor_data = soilingSensorDataArray;
  }

  return cleanedData;
};
